import React from 'react';
import { PersonStandingIcon, ArmchairIcon, FootprintsIcon, HandIcon, UsersIcon, CheckIcon, BrainIcon } from 'lucide-react';
import type { PageVariant } from '../../data/content';
type PagePreviewProps = {
  variant: PageVariant;
  title: string;
  className?: string;
};
export function PagePreview({
  variant,
  title,
  className = ''
}: PagePreviewProps) {
  return <div className={`flex aspect-[3/4] flex-col overflow-hidden rounded-lg bg-white p-[9%] shadow-[0_14px_30px_-14px_rgba(23,49,59,0.45)] ring-1 ring-ink/5 ${className}`} aria-hidden="true" data-id="element-43" data-mp="cp41efs0w4s2">
      <div className="flex items-center gap-1" data-id="element-44" data-mp="3h3c85i549i35">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" data-id="element-45" data-mp="3v1sdp5r8t2jp" />
        <span className="truncate font-display text-[7px] font-extrabold uppercase tracking-wide text-brand-800 sm:text-[8px]" data-id="element-46" data-mp="3q1xm0zzn88xl">
          {title}
        </span>
      </div>
      <div className="mt-[9%] min-h-0 flex-1" data-id="element-47" data-mp="1acmfm64gdz3g">
        <PageBody variant={variant} data-id="element-48" data-mp="15c8c91dsrdbr" />
      </div>
      <div className="flex items-center justify-between pt-1" data-id="element-49" data-mp="15djfn03byj2c">
        <span className="font-display text-[5px] font-bold text-brand-700 sm:text-[6px]" data-id="element-50" data-mp="395jpihgjgsn4">REACTIVA TU CUERPO™</span>
        <span className="h-1 w-3 rounded bg-brand-100" data-id="element-51" data-mp="3e3v3939yfnam" />
      </div>
    </div>;
}
function Line({
  w = 'w-full',
  tone = 'bg-ink/10'
}: {
  w?: string;
  tone?: string;
}) {
  return <span className={`block h-[3px] rounded-full ${tone} ${w}`} data-id="element-52" data-mp="3o2kgujutill2" />;
}
function PageBody({
  variant
}: {
  variant: PageVariant;
}) {
  switch (variant) {
    case 'exercise':
      {
        const icons = [PersonStandingIcon, ArmchairIcon, FootprintsIcon, HandIcon];
        return <div className="grid h-full grid-cols-2 gap-1" data-id="element-53" data-mp="30dhw334w0cmc">
          {icons.map((Icon, i) => <div key={i} className="flex flex-col items-center justify-center gap-1 rounded bg-brand-50 p-1" data-id="element-54" data-mp="354hp3xavyajk" data-mpi={i}>
              <Icon className="h-1/2 w-1/2 text-brand-600" strokeWidth={1.75} data-id="element-55" data-mp="27pvprn49ytvs" />
              <Line w="w-3/4" data-id="element-56" data-mp="2cogsbt701pne" />
            </div>)}
        </div>;
      }
    case 'routine':
      return <div className="flex h-full flex-col justify-between" data-id="element-57" data-mp="24nr9bmyf33t3">
          {[1, 2, 3, 4, 5].map(n => <div key={n} className="flex items-center gap-1" data-id="element-58" data-mp="24lei0j6rrh3d">
              <span className="flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[5px] font-bold text-white" data-id="element-59" data-mp="1znwjlmqdok9m">
                {n}
              </span>
              <Line w="flex-1" data-id="element-60" data-mp="29fioz2g1bp4g" />
              <span className="text-[5px] font-bold text-brand-700" data-id="element-61" data-mp="29f98c5gvqubf">x10</span>
            </div>)}
        </div>;
    case 'table':
      return <div className="flex h-full flex-col overflow-hidden rounded-sm ring-1 ring-brand-100" data-id="element-62" data-mp="1gptqowupm36j">
          <div className="grid grid-cols-4 bg-brand-600" data-id="element-63" data-mp="1vn2uwr5431fo">
            {[0, 1, 2, 3].map(c => <span key={c} className="h-2" data-id="element-64" data-mp="1vkq2tl5n3aow" />)}
          </div>
          {[0, 1, 2, 3, 4].map(r => <div key={r} className="grid flex-1 grid-cols-4 border-t border-brand-100" data-id="element-65" data-mp="1qmonnbbc3vip">
              {[0, 1, 2, 3].map(c => <span key={c} className="flex items-center border-l border-brand-100 px-0.5 first:border-l-0" data-id="element-66" data-mp="3742lnibt987l">
                  {c === 0 ? <Line w="w-full" data-id="element-67" data-mp="39mxj3iflhctc" /> : (r + c) % 3 === 0 ? <CheckIcon className="h-1.5 w-1.5 text-brand-600" strokeWidth={4} data-id="element-68" data-mp="39lwb5mtvl6fu" /> : null}
                </span>)}
            </div>)}
        </div>;
    case 'checklist':
      return <div className="flex h-full flex-col justify-between" data-id="element-69" data-mp="30mrc8feejb35">
          {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="flex items-center gap-1" data-id="element-70" data-mp="34n1h1oqgjvgf">
              <span className={`flex h-2 w-2 shrink-0 items-center justify-center rounded-[2px] ${i < 4 ? 'bg-brand-600' : 'ring-1 ring-brand-300'}`} data-id="element-71" data-mp="34pe7yre71g5m">
                {i < 4 && <CheckIcon className="h-1.5 w-1.5 text-white" strokeWidth={4} data-id="element-72" data-mp="15jadkmf8uhl" />}
              </span>
              <Line w={i % 2 === 0 ? 'w-full' : 'w-3/4'} data-id="element-73" data-mp="3jk2b3yw4g2i2" />
            </div>)}
        </div>;
    case 'calendar':
      return <div className="flex h-full flex-col gap-0.5" data-id="element-74" data-mp="3nkm72z4yl02m">
          <div className="grid grid-cols-7 gap-0.5 text-center text-[5px] font-bold text-brand-700" data-id="element-75" data-mp="f63wapnmv6fb">
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <span key={i} data-id="element-76" data-mp="jzgfgk9jml0o" data-mpi={i}>{d}</span>)}
          </div>
          <div className="grid flex-1 grid-cols-7 grid-rows-4 gap-0.5" data-id="element-77" data-mp="2seoifzvjl36z">
            {Array.from({
            length: 28
          }).map((_, i) => <span key={i} className={`flex items-center justify-center rounded-[2px] ${i < 17 ? i % 7 > 4 ? 'bg-brand-100' : 'bg-brand-500' : 'bg-brand-50'}`} data-id="element-78" data-mp="2ne0k0t4zhk2q" data-mpi={i}>
                {i < 17 && i % 7 <= 4 && <CheckIcon className="h-1.5 w-1.5 text-white" strokeWidth={4} data-id="element-79" data-mp="2eg83xmrfu8ea" />}
              </span>)}
          </div>
        </div>;
    case 'planner':
      return <div className="flex h-full flex-col gap-1" data-id="element-80" data-mp="365b70z7l9cpc">
          {[{
          label: 'Entrada en calor',
          min: "10'",
          cls: 'flex-[2] bg-brand-100 text-brand-800'
        }, {
          label: 'Parte central',
          min: "25'",
          cls: 'flex-[4] bg-brand-600 text-white'
        }, {
          label: 'Vuelta a la calma',
          min: "10'",
          cls: 'flex-[2] bg-sun-100 text-ink'
        }].map(b => <div key={b.label} className={`flex items-start justify-between rounded px-1 py-0.5 ${b.cls}`} data-id="element-81" data-mp="3p3vv0ie6o1yr">
              <span className="text-[5px] font-bold leading-tight sm:text-[6px]" data-id="element-82" data-mp="1bnm263b64w92">{b.label}</span>
              <span className="text-[5px] font-extrabold sm:text-[6px]" data-id="element-83" data-mp="1glxf4mwrfmvi">{b.min}</span>
            </div>)}
        </div>;
    case 'cognitive':
      return <div className="flex h-full flex-col gap-1" data-id="element-84" data-mp="1plsrgv21xnx4">
          <div className="grid flex-1 grid-cols-3 gap-1" data-id="element-85" data-mp="1pmltxjtz9r9g">
            {['bg-brand-500 rounded-full', 'bg-sun rounded-sm', 'bg-brand-200 rounded-full', 'bg-brand-800 rounded-sm', 'bg-brand-400 rounded-full', 'bg-sun rounded-full', 'bg-brand-200 rounded-sm', 'bg-brand-600 rounded-full', 'bg-brand-300 rounded-sm'].map((cls, i) => <span key={i} className={`m-auto aspect-square w-3/4 ${cls}`} data-id="element-86" data-mp="24bi5vrz5nofc" data-mpi={i} />)}
          </div>
          <div className="flex items-center gap-1" data-id="element-87" data-mp="28bim9i5te9p1">
            <BrainIcon className="h-2.5 w-2.5 shrink-0 text-brand-600" data-id="element-88" data-mp="2dbwup1bbvlnl" />
            <Line data-id="element-89" data-mp="2d9k6w7iw2h2m" />
          </div>
        </div>;
    case 'group':
      return <div className="relative h-full" data-id="element-90" data-mp="2iard425dv876">
          {Array.from({
          length: 8
        }).map((_, i) => {
          const angle = i / 8 * Math.PI * 2;
          return <span key={i} className={`absolute h-[14%] w-[14%] -translate-x-1/2 -translate-y-1/2 rounded-full ${i % 3 === 0 ? 'bg-sun' : 'bg-brand-500'}`} style={{
            left: `${50 + Math.cos(angle) * 36}%`,
            top: `${50 + Math.sin(angle) * 36}%`
          }} data-id="element-91" data-mp="d9x2wf374lzs" data-mpi={i} />;
        })}
          <span className="absolute left-1/2 top-1/2 flex h-[30%] w-[30%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-50" data-id="element-92" data-mp="10xyfq07eqol0">
            <UsersIcon className="h-1/2 w-1/2 text-brand-700" data-id="element-93" data-mp="wzh536os2vyu" />
          </span>
        </div>;
    case 'guide':
      return <div className="flex h-full flex-col gap-1" data-id="element-94" data-mp="15y2m2xelq2xi">
          <Line w="w-2/3" tone="bg-brand-700/60" data-id="element-95" data-mp="15ycgcx6oiw90" />
          <Line data-id="element-96" data-mp="3cnqyvrnb0b95" />
          <Line data-id="element-97" data-mp="3cnhau9ltke63" />
          <Line w="w-4/5" data-id="element-98" data-mp="3cp25dz84vx3y" />
          <div className="mt-0.5 flex flex-1 flex-col justify-center gap-1 rounded bg-sun-100 p-1" data-id="element-99" data-mp="3cqd7lulxkwsy">
            <Line w="w-1/2" tone="bg-ink/25" data-id="element-100" data-mp="37ra8srwjnmtf" />
            <Line tone="bg-ink/10" data-id="element-101" data-mp="1n6vfhzmk41j0" />
          </div>
          <Line data-id="element-102" data-mp="1n9hlvvxpoazn" />
          <Line w="w-3/5" data-id="element-103" data-mp="1s689r5frcavm" />
        </div>;
    case 'card':
      return <div className="flex h-full flex-col gap-1" data-id="element-104" data-mp="1s5gexwtzih6e">
          <div className="flex flex-[3] items-center justify-center rounded bg-brand-50" data-id="element-105" data-mp="1dhvbu2x40hyh">
            <PersonStandingIcon className="h-3/5 w-3/5 text-brand-600" strokeWidth={1.75} data-id="element-106" data-mp="1ifwwgsa6opc2" />
          </div>
          <div className="flex flex-[2] flex-col justify-between" data-id="element-107" data-mp="14g47b1eb2h67">
            {['Series', 'Reps', 'Nivel'].map(f => <div key={f} className="flex items-center gap-1" data-id="element-108" data-mp="19g0ch3yqoenc">
                <span className="w-5 text-[5px] font-bold text-brand-700" data-id="element-109" data-mp="uihgphnn916e">{f}</span>
                <Line w="flex-1" data-id="element-110" data-mp="ukkit022mkrh" />
              </div>)}
          </div>
        </div>;
    default:
      return null;
  }
}